/**
 * {paymentNotice}の紙の画面 /payment-notices/:id/paper（#233 3b、docs/93 §15.6）。発行したら作成画面からここへ移る（送付）。
 *   左＝紙（下書きは透かし入り。出した紙は保存した PDF）
 *   右＝送信方法［印刷・FAX｜メール｜リンク］と、その方法の枠（送付状・メール・確認ページのリンク）、相手の確認（確認の期限・確認の記録・誤りがある）
 *   見出し＝題・状態チップ・PDF を保存・主ボタン（印刷／送信／リンクをコピー）・⋯（取消・リンクを出し直す・仕入を開く）
 *   送付の記録は今のまま（document_deliveries。リンク＝method link・送付状なしの印刷＝method print）＝確認の期限（送付後 n日）の起点。
 *   スマホ（767px 以下）は紙を見るだけ
 */
import { MAIL_MSG, MSG, PAYMENT_NOTICE_MSG, coverItemKey, type DeliveryItemRef, type PaymentNotice, type UnissueResult } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { api, fetchFile, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { CoverBundlePicker, useItemPages } from "../../components/CoverBundle.jsx";
import { CoverLetterPanel, contentOf, useCoverLetter } from "../../components/CoverLetterPanel.jsx";
import { HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { MailPanel, MailPreview, MailSendDialog, useMailDelivery } from "../../components/MailPanel.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { UnissueDialog } from "../../components/UnissueDialog.jsx";
import { ErrorText, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { mdSlash } from "../../lib/num.js";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../../lib/pdfPages.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { jstDay } from "../orders/parts.jsx";
import { NoticeStateChip } from "./parts.jsx";
import { noticeVendorText } from "./PaymentNoticePage.jsx";

import { ymdHmShort, ymdShort } from "../../lib/dates.js";
type Via = "print" | "mail" | "link";

export function PaymentNoticePaperPage() {
  const { id = "" } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const q = useQuery({ queryKey: ["payment-notice", id], queryFn: () => api<PaymentNotice>("GET", `/payment-notices/${id}`), retry: false });
  const n = q.data;
  const label = n?.names.paymentNotice ?? t(PAYMENT_NOTICE_MSG.list);
  const short = n ? docShort(label, n.displayNo ?? n.documentNo) : null;
  usePageName(label, short);
  const back = useBackNav({ to: `/payment-notices/${id}`, label: short ?? label });
  const narrow = useNarrow();
  const pc = !narrow && currentView() !== "phone";
  const draft = n?.status === "draft";
  const issued = n?.status === "issued";
  // 初めの送信方法：移ってきたときに決めた方法 → {vendor}の送信設定の「送信方法」（#303 B2。送付状の初めの値と一緒に届く＝下の useEffect）
  const [viaSel, setVia] = useState<Via | null>(() => {
    const v = (loc.state as { via?: string } | null)?.via;
    return v === "mail" || v === "link" ? v : null;
  });
  // リンクは確認・誤りがあるの前だけ
  const linkOk = !!n?.link?.active && !n?.confirm && !n?.dispute;
  const via: Via = viaSel === "link" && !linkOk ? "print" : (viaSel ?? "print");
  const pdfUrl = `/api/v1/payment-notices/${id}/pdf`;
  const target = n && issued && pc ? { items: [{ kind: "payment_notice" as const, id }] as DeliveryItemRef[] } : null;
  const cover = useCoverLetter(via === "print" ? target : null);
  // {vendor}の送信方法の既定（#303 B2）：初めの値が届いたら1回だけ。届くまで送信方法は選べない形で待つ（押す所をずらさない＝#179）
  const viaKnown = viaSel !== null || !target || cover.draft.isError;
  useEffect(() => {
    if (viaSel === null && cover.draft.data) setVia(cover.draft.data.sendMethod);
  }, [viaSel, cover.draft.data]);
  const coverOn = !!target && via === "print" && cover.on;
  const [mailExtra, setMailExtra] = useState<DeliveryItemRef[]>([]);
  const [mailPicking, setMailPicking] = useState(false);
  const mailTarget = via === "mail" && target ? { items: [...target.items, ...mailExtra] } : null;
  const extraPages = useItemPages(via === "mail" ? mailExtra : []);
  const moveExtra = (i: number, dir: -1 | 1) =>
    setMailExtra((xs) => {
      const j = i + dir;
      if (j < 0 || j >= xs.length) return xs;
      const next = xs.slice();
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const mail = useMailDelivery(mailTarget);
  const mailCover = useCoverLetter(mailTarget, { mail: true });
  const mailCoverOn = via === "mail" && mailCover.on;

  const [pages, setPages] = useState<PageImg[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  useEffect(() => {
    if (!n) return;
    let alive = true;
    let made: string | null = null;
    setPages([]);
    setError(undefined);
    setLoading(true);
    setBlobUrl(null);
    (async () => {
      try {
        const blob = await fetchFile(pdfUrl);
        if (!alive) return;
        made = URL.createObjectURL(blob);
        setBlobUrl(made);
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          if (!alive) return;
          setPages((ps) => {
            const next = ps.slice();
            next[i - 1] = p;
            return next;
          });
        });
      } catch (e) {
        if (alive) setError(messageOf(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [n?.revision.id, n?.status, pdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  useEffect(() => {
    if (!printPages) return;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(r);
  }, [printPages]);
  const [busy, setBusy] = useState(false);
  const refresh = (next: PaymentNotice) => {
    qc.setQueryData(["payment-notice", id], next);
    void qc.invalidateQueries({ queryKey: ["payment-notices"] });
    void qc.invalidateQueries({ queryKey: ["deliveries"] });
  };
  const deliverCover = async (method: "print" | "download") => {
    setBusy(true);
    try {
      const { result, blob } = await cover.deliver(method);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      // 送付状の送付の記録が確認の期限の起点（状態を読み直す）
      void qc.invalidateQueries({ queryKey: ["payment-notice", id] });
      if (method === "download") downloadBlob(blob, result.fileName);
      else {
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        setPrintPages(await portraitForPrint(got));
      }
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  /** 送った（リンク・送付状なしの印刷）＝送付の記録 */
  const sent = async (v: "copy" | "line" | "sms" | "print") => {
    try {
      refresh(await api<PaymentNotice>("POST", `/payment-notices/${id}/sent`, { via: v }));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const doPrint = () => {
    if (!pages.length) return;
    if (coverOn) return void deliverCover("print");
    setPrintPages(null);
    window.print();
    if (issued) void sent("print");
  };
  /* ---- メール ---- */
  const [mailConfirm, setMailConfirm] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailError, setMailError] = useState<string | undefined>();
  const sendMail = async () => {
    setMailBusy(true);
    setMailError(undefined);
    try {
      await mail.send(mailCoverOn && mailCover.form ? contentOf(mailCover.form) : null);
      setMailConfirm(false);
      toast(MAIL_MSG.sent);
    } catch (e) {
      setMailConfirm(false);
      setMailError(messageOf(e));
    } finally {
      setMailBusy(false);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      void qc.invalidateQueries({ queryKey: ["mail-draft"] });
      void qc.invalidateQueries({ queryKey: ["payment-notice", id] });
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
    }
  };
  /* ---- リンク（コピー・LINE・SMS） ---- */
  const linkUrl = linkOk ? (n?.link?.url ?? null) : null;
  const copyLink = async () => {
    if (!linkUrl) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(linkUrl);
      ok = true;
    } catch {
      const el = document.querySelector<HTMLInputElement>("[data-testid=notice-link-url]");
      if (el) {
        el.focus();
        el.select();
        try {
          ok = document.execCommand("copy");
        } catch {
          ok = false;
        }
      }
    }
    if (!ok) {
      toast(PAYMENT_NOTICE_MSG.linkCopyFailed, undefined, "error", "link-copy-failed");
      return;
    }
    toast(PAYMENT_NOTICE_MSG.linkCopied, undefined, "success", "link-copied");
    await sent("copy");
  };
  const linkText = n && linkUrl ? `${n.names.paymentNotice} ${n.displayNo ?? n.documentNo ?? ""}\n${linkUrl}` : "";
  const reissue = async () => {
    try {
      refresh(await api<PaymentNotice>("POST", `/payment-notices/${id}/reissue-link`, {}));
      toast(PAYMENT_NOTICE_MSG.linkReissued);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 取消し（取消しの窓＝見積書・請求書・{order}と同じ UnissueDialog）。確認の前だけ */
  const [unissueDlg, setUnissueDlg] = useState<{ busy: boolean; error?: string | undefined } | null>(null);
  const unissue = async () => {
    setUnissueDlg({ busy: true });
    try {
      const r = await api<UnissueResult>("POST", `/payment-notices/${id}/unissue`, {});
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
      await qc.invalidateQueries({ queryKey: ["payment-notice", id] });
      setUnissueDlg(null);
      nav(`/payment-notices/${id}`, { state: { notice: t(PAYMENT_NOTICE_MSG.unissued).replace("{paymentNotice}", label).replace("{no}", r.documentNo), noticeTestId: "unissued-notice" } });
    } catch (e) {
      setUnissueDlg({ busy: false, error: messageOf(e) });
    }
  };

  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/payment-notices">{t(PAYMENT_NOTICE_MSG.list)}</Link>
        </p>
      </div>
    );
  }
  const subject = n?.revision.subject || label;
  const docNo = n ? (n.displayNo ?? n.documentNo) : null;
  const fileName = n ? `${label}_${docNo && !draft ? docNo : "下書き"}_${subject}.pdf` : `${label}.pdf`;
  const actions: HeadAction[] = !n
    ? []
    : [
        ...(n.purchase ? [{ label: PAYMENT_NOTICE_MSG.purchaseOpen, to: `/purchases/${n.purchase.id}`, testId: "notice-purchase-open", group: "state" as const }] : []),
        ...(issued && n.canUnissue ? [{ label: PAYMENT_NOTICE_MSG.unissue, onSelect: () => setUnissueDlg({ busy: false }), testId: "notice-unissue", group: "state" as const }] : []),
        ...(issued && n.link && !n.confirm && !n.dispute ? [{ label: PAYMENT_NOTICE_MSG.linkReissue, onSelect: () => void reissue(), testId: "notice-link-reissue", group: "state" as const }] : []),
      ];
  return (
    <div className="paperview">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{label}</h1>
        {n ? <NoticeStateChip state={n.state} waitingDays={n.waitingDays} confirmedAt={n.confirm?.at ?? null} documentNo={n.documentNo} deadline={n.deadline} /> : null}
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {n ? `${docNo && !draft ? docNo + "　" : ""}${subject}` : "読み込んでいます…"}
        </span>
        <span className="sp" />
        {coverOn ? (
          <button type="button" className="btn" disabled={!blobUrl || busy || !cover.ready} onClick={() => void deliverCover("download")} data-testid="paper-save">PDF 保存</button>
        ) : (
          <a className={"btn" + (blobUrl ? "" : " disabled")} href={blobUrl ?? undefined} download={fileName} aria-disabled={!blobUrl} data-testid="paper-save">PDF 保存</a>
        )}
        {target && via === "mail" ? (
          <button type="button" className="btn primary" onClick={() => setMailConfirm(true)} disabled={!mail.ready || mail.problems.length > 0 || mailBusy || mail.uploading || (mailCoverOn && !mailCover.ready)} data-testid="mail-send">
            {mailBusy ? MAIL_MSG.sending : MAIL_MSG.send}
          </button>
        ) : target && via === "link" ? (
          <button type="button" className="btn primary" onClick={() => void copyLink()} disabled={!linkUrl} data-testid="notice-link-copy">{PAYMENT_NOTICE_MSG.linkCopy}</button>
        ) : (
          <button type="button" className="btn primary" onClick={doPrint} disabled={loading || !pages.length || !!error || busy || (coverOn && !cover.ready)} data-testid="paper-print">
            {busy ? "送付状作成中…" : "印刷"}
          </button>
        )}
        {actions.length ? <span className="hd-acts" data-testid="head-actions"><HeadActions actions={actions} label={t(PAYMENT_NOTICE_MSG.stateMenu).replace("{paymentNotice}", label)} /></span> : null}
      </div>
      <ErrorText>{error}</ErrorText>
      {loading && !pages.length && !error ? <p className="hint" role="status" data-testid="paper-making">{MSG.pdfMaking}</p> : null}
      <div className={target ? "paperwrap" : undefined}>
        <div className="pages" aria-label={`${label}のページ`}>
          {target && via === "mail" ? <MailPreview m={mail} companyName={mail.draft.data?.values.company ?? ""} /> : null}
          {coverOn || mailCoverOn
            ? (via === "mail" ? mailCover : cover).pages.map((p, i) => (
                <div className="clsheet" key={`c${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {pages.map((p, i) =>
            p ? (
              <div className="paperpage" key={i}>
                <img src={p.url} alt={`${subject} ${i + 1}ページ目`} width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
          {via === "mail"
            ? extraPages.flatMap((q2, k) =>
                (q2.data ?? []).map((p, i) => (
                  <div className="paperpage" key={`x${k}-${i}`} data-testid="mail-extra-sheet">
                    <img src={p.url} alt={`追加した書類 ${k + 1} の ${i + 1}ページ目`} width={p.w} height={p.h} />
                  </div>
                )),
              )
            : null}
        </div>
        {target && n ? (
          <div className="sendcol" data-testid="send-column">
            <div className={"clpanel sendvia" + (viaKnown ? "" : " is-wait")} aria-busy={viaKnown ? undefined : true}>
              <span className="cllbl" id="pnsendvia-l">{PAYMENT_NOTICE_MSG.via}</span>
              <Segmented
                options={[
                  { value: "print" as const, label: MAIL_MSG.viaPrint, testId: "send-via-print" },
                  { value: "mail" as const, label: MAIL_MSG.viaMail, testId: "send-via-mail" },
                  ...(linkOk ? [{ value: "link" as const, label: PAYMENT_NOTICE_MSG.viaLink, testId: "send-via-link" }] : []),
                ]}
                value={via}
                onChange={setVia}
                labelledBy="pnsendvia-l"
                testId="send-via"
              />
            </div>
            <ConfirmPanel n={n} />
            {via === "mail" ? (
              <>
                <MailPanel m={mail} bundle={{ extra: mailExtra, onMove: moveExtra, onRemove: (i) => setMailExtra((xs) => xs.filter((_, j) => j !== i)), onAddDocs: () => setMailPicking(true) }} error={mailError} />
                <CoverLetterPanel c={mailCover} showWindow={false} testId="mail-cover-panel" />
              </>
            ) : via === "link" ? (
              <div className="clpanel olink" data-testid="notice-link-panel">
                <div className="cllbl">{PAYMENT_NOTICE_MSG.viaLink}</div>
                {linkUrl ? (
                  <>
                    <input className="inp" readOnly value={linkUrl} aria-label={PAYMENT_NOTICE_MSG.viaLink} onFocus={(e) => e.target.select()} data-testid="notice-link-url" />
                    <p className="hint" style={{ margin: "6px 0" }}>{PAYMENT_NOTICE_MSG.linkExpires.replace("{date}", mdSlash(jstDay(n.link!.expiresAt)))}</p>
                    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                      <a className="btn sm" href={`https://line.me/R/msg/text/?${encodeURIComponent(linkText)}`} target="_blank" rel="noreferrer" onClick={() => void sent("line")} data-testid="notice-link-line">{PAYMENT_NOTICE_MSG.linkLine}</a>
                      <a className="btn sm" href={`sms:?&body=${encodeURIComponent(linkText)}`} onClick={() => void sent("sms")} data-testid="notice-link-sms">{PAYMENT_NOTICE_MSG.linkSms}</a>
                    </div>
                  </>
                ) : (
                  <p className="hint" style={{ margin: 0 }}>{PAYMENT_NOTICE_MSG.linkNone}</p>
                )}
              </div>
            ) : (
              <CoverLetterPanel c={cover} />
            )}
          </div>
        ) : null}
      </div>
      {mailTarget && mailPicking ? (
        <CoverBundlePicker
          target={mailTarget}
          onClose={() => setMailPicking(false)}
          onPick={(refs, labels) => {
            setMailPicking(false);
            setMailExtra((xs) => [...xs, ...refs.filter((r) => !xs.some((x) => coverItemKey(x) === coverItemKey(r))).map((r) => ((r.kind === "received" || r.kind === "file") && labels[coverItemKey(r)] ? { ...r, label: labels[coverItemKey(r)] } : r))]);
          }}
        />
      ) : null}
      {mailConfirm && mail.form ? <MailSendDialog m={mail} cover={mailCoverOn} busy={mailBusy} onClose={() => setMailConfirm(false)} onSend={() => void sendMail()} /> : null}
      {unissueDlg && n ? <UnissueDialog doc="payment_notice" no={n.displayNo ?? n.documentNo ?? ""} busy={unissueDlg.busy} error={unissueDlg.error} onClose={() => setUnissueDlg(null)} onConfirm={() => void unissue()} /> : null}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(coverOn && printPages ? printPages : pages).map((p, i) =>
            p ? (
              <div className="printedPage" key={i}>
                <img src={p.url} alt="" width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** 右の枠の「相手の確認」：登録番号（控除割合）・確認の期限・確認の記録・誤りがある */
function ConfirmPanel({ n }: { n: PaymentNotice }) {
  const c = n.confirm;
  return (
    <div className="clpanel oackrec" data-testid="notice-confirm-panel">
      <div className="cllbl">{PAYMENT_NOTICE_MSG.confirmPanel}</div>
      <p className="hint" style={{ margin: "0 0 6px" }} data-testid="notice-panel-vendor-tax">{noticeVendorText(n)}</p>
      {c ? (
        <dl className="odl" data-testid="notice-confirm-record">
          <dt>{PAYMENT_NOTICE_MSG.confirmMethod}</dt>
          <dd>{c.method === "expired" ? PAYMENT_NOTICE_MSG.confirmExpired : PAYMENT_NOTICE_MSG.confirmScreen}</dd>
          <dt>{PAYMENT_NOTICE_MSG.confirmAt}</dt>
          <dd data-testid="notice-confirm-at">{ymdHmShort(c.at)}</dd>
          {c.email ? (
            <>
              <dt>{PAYMENT_NOTICE_MSG.confirmBy}</dt>
              <dd>{c.email}</dd>
            </>
          ) : null}
          <dt>{PAYMENT_NOTICE_MSG.confirmRecordNo}</dt>
          <dd className="num" data-testid="notice-record-no">{c.recordNo}</dd>
        </dl>
      ) : n.dispute ? (
        <div data-testid="notice-dispute">
          <p className="shu" style={{ margin: "0 0 4px", fontWeight: 700 }}>{PAYMENT_NOTICE_MSG.disputeRecord}</p>
          <p style={{ margin: "0 0 4px", whiteSpace: "pre-wrap" }} data-testid="notice-dispute-reason">{n.dispute.reason}</p>
          <p className="hint" style={{ margin: 0 }}>{PAYMENT_NOTICE_MSG.disputeHint}</p>
        </div>
      ) : (
        <p className="hint" style={{ margin: 0 }} data-testid="notice-deadline">
          {PAYMENT_NOTICE_MSG.confirmDeadline}{" "}
          {n.deadline ? <b className="num">{ymdShort(n.deadline)}</b> : PAYMENT_NOTICE_MSG.confirmDeadlineNone.replace("{days}", String(n.confirmDays))}
        </p>
      )}
      {n.purchase ? (
        <p style={{ margin: "6px 0 0" }}>
          <Link className="btn sm" to={`/purchases/${n.purchase.id}`} data-testid="notice-panel-purchase">{PAYMENT_NOTICE_MSG.purchaseOpen}</Link>
        </p>
      ) : null}
    </div>
  );
}
